/**
 * Whānau exit-feedback survey — content model (bilingual).
 *
 * Source of truth for the survey's questions, order, scales and option lists.
 * The public survey form (/survey/exit) renders from this; the storage columns
 * in docs/schema/exit-survey.sql mirror the `id`s here. See ADR-008.
 *
 * te reo Māori (`mi`) strings are DRAFT or `null` and MUST be reviewed by a
 * fluent speaker before whānau testing (docs/Te-Reo-Review-for-Des.docx).
 * Nuanced wording is intentionally left `null` rather than machine-guessed;
 * English (`en`) is taken from the approved document.
 *
 * Privacy: this file is question content only — no PII. Responses are stored
 * anonymously (ADR-008 §2.1).
 *
 * NOTE (ADR-008 Q4): the rating scale (1–5) and the demographic option lists
 * below are provisional and need NASH confirmation. The `services_involved`
 * options are populated at runtime from the service directory.
 */

/** A bilingual string. `mi: null` means "awaiting te reo review". */
export type Bilingual = { en: string; mi: string | null };

export type RatingQuestion = {
  id: string;
  kind: "rating";
  prompt: Bilingual;
  lowAnchor: Bilingual;
  highAnchor: Bilingual;
  /** Discrete 1..scaleMax radio scale. */
  scaleMax: 5;
};

export type TextQuestion = {
  id: string;
  kind: "text";
  prompt: Bilingual;
  maxLength: number;
};

export type ShortTextQuestion = {
  id: string;
  kind: "shortText";
  prompt: Bilingual;
  maxLength: number;
};

export type NumberQuestion = {
  id: string;
  kind: "number";
  prompt: Bilingual;
  min: number;
  max: number;
};

export type ChoiceOption = { value: string; label: Bilingual };

export type ChoiceQuestion = {
  id: string;
  kind: "single" | "multi";
  prompt: Bilingual;
  /** Static options, or [] when populated from `optionsSource`. */
  options: ChoiceOption[];
  /** Dynamic option source resolved at render time. */
  optionsSource?: "service-directory";
  /** Permit a free-text "other" entry. */
  allowOther?: boolean;
};

export type Question =
  | RatingQuestion
  | TextQuestion
  | ShortTextQuestion
  | NumberQuestion
  | ChoiceQuestion;

export type Section = {
  id: string;
  title: Bilingual;
  intro?: Bilingual;
  questions: Question[];
};

export type SurveyContent = {
  id: "exit";
  /** Two-letter default language; the form lets whānau switch. */
  defaultLanguage: "mi" | "en";
  title: Bilingual;
  intro: Bilingual[];
  confidentialityNote: Bilingual;
  sections: Section[];
  closing: Bilingual;
  /** Shown beside the optional name/contact fields. */
  leaveNameNote: Bilingual;
};

/** 1–5 anchored rating questions (anchors verbatim from the document). */
const RATING_QUESTIONS: RatingQuestion[] = [
  {
    id: "positive_experience",
    kind: "rating",
    prompt: {
      en: "Has your time with Te Tohu o Te Ora o Ngāti Awa been a positive experience for you?",
      mi: null,
    },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Totally positive", mi: null },
    scaleMax: 5,
  },
  {
    id: "met_expectations",
    kind: "rating",
    prompt: { en: "Did our service/s meet your expectations?", mi: null },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Above expectation", mi: null },
    scaleMax: 5,
  },
  {
    id: "learn_skills",
    kind: "rating",
    prompt: {
      en: "Did you have opportunities to learn new skills, gain more knowledge?",
      mi: null,
    },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Always", mi: null },
    scaleMax: 5,
  },
  {
    id: "kaimahi_relationship",
    kind: "rating",
    prompt: { en: "What was your relationship with kaimahi like?", mi: null },
    lowAnchor: { en: "Difficult", mi: null },
    highAnchor: { en: "Best", mi: null },
    scaleMax: 5,
  },
  {
    id: "felt_safe",
    kind: "rating",
    prompt: { en: "Did you feel safe and comfortable with us?", mi: null },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Always", mi: null },
    scaleMax: 5,
  },
  {
    id: "supported_goals",
    kind: "rating",
    prompt: {
      en: "Did we support you to work on goals that were important to you?",
      mi: null,
    },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Always", mi: null },
    scaleMax: 5,
  },
  {
    id: "supported_aspirations",
    kind: "rating",
    prompt: { en: "Did we support your aspirations and hopes?", mi: null },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Always", mi: null },
    scaleMax: 5,
  },
  {
    id: "listened_respect",
    kind: "rating",
    prompt: {
      en: "Did you feel listened to, with courtesy, respect, and dignity?",
      mi: null,
    },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Always", mi: null },
    scaleMax: 5,
  },
  {
    id: "met_needs",
    kind: "rating",
    prompt: { en: "Was our service/s able to meet your needs?", mi: null },
    lowAnchor: { en: "None were met", mi: null },
    highAnchor: { en: "All were met", mi: null },
    scaleMax: 5,
  },
  {
    id: "resilient_confident",
    kind: "rating",
    prompt: {
      en: "Do you feel more resilient and confident and better able to make decisions for you and your whānau?",
      mi: null,
    },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "A lot more", mi: null },
    scaleMax: 5,
  },
  {
    id: "motivation",
    kind: "rating",
    prompt: {
      en: "How motivated were you to achieve your goals and make changes?",
      mi: null,
    },
    lowAnchor: { en: "Unmotivated", mi: null },
    highAnchor: { en: "Highly motivated", mi: null },
    scaleMax: 5,
  },
  {
    id: "cultural_identity",
    kind: "rating",
    prompt: {
      en: "Do you feel that your cultural identity was supported by services based on Kaupapa Māori principles and Ngāti Awa tikanga?",
      mi: null,
    },
    lowAnchor: { en: "Not at all", mi: null },
    highAnchor: { en: "Very supported", mi: null },
    scaleMax: 5,
  },
  {
    id: "future_engagement",
    kind: "rating",
    prompt: {
      en: "Would you likely engage with our services, or other services, when you need to?",
      mi: null,
    },
    lowAnchor: { en: "Not at all likely", mi: null },
    highAnchor: { en: "Very likely", mi: null },
    scaleMax: 5,
  },
];

/** Open-text reflections. */
const OPEN_TEXT_QUESTIONS: TextQuestion[] = [
  {
    id: "most_proud",
    kind: "text",
    prompt: { en: "What were you most proud of?", mi: null },
    maxLength: 2000,
  },
  {
    id: "what_helped",
    kind: "text",
    prompt: { en: "What helped you and your whānau most?", mi: null },
    maxLength: 2000,
  },
  {
    id: "got_in_way",
    kind: "text",
    prompt: {
      en: "What, if anything, got in the way of change for you and your whānau?",
      mi: null,
    },
    maxLength: 2000,
  },
  {
    id: "improve",
    kind: "text",
    prompt: {
      en: "What could we do differently to improve our service? Feel free to expand on your answers above.",
      mi: null,
    },
    maxLength: 2000,
  },
];

export const exitSurvey: SurveyContent = {
  id: "exit",
  defaultLanguage: "mi",
  title: { en: "Whānau exit feedback", mi: null },
  intro: [
    { en: "A heartfelt greeting to you.", mi: "Ka nui te mihi ki a koe" },
    {
      en: "To make sure we are providing quality services to the whānau we work with, we would appreciate your feedback so we know the impact we are having and ways we can improve.",
      mi: null,
    },
  ],
  confidentialityNote: {
    en: "Information provided remains confidential.",
    mi: null,
  },
  sections: [
    {
      id: "about_you",
      title: { en: "About you", mi: null },
      questions: [
        {
          id: "gender",
          kind: "single",
          prompt: { en: "Gender", mi: null },
          // Provisional — confirm with NASH (ADR-008 Q4).
          options: [
            { value: "female", label: { en: "Female", mi: null } },
            { value: "male", label: { en: "Male", mi: null } },
            {
              value: "another-gender",
              label: { en: "Another gender", mi: null },
            },
            {
              value: "prefer-not-to-say",
              label: { en: "Prefer not to say", mi: null },
            },
          ],
        },
        {
          id: "age_range",
          kind: "single",
          prompt: { en: "Age range", mi: null },
          options: [
            { value: "under-25", label: { en: "Under 25", mi: null } },
            { value: "25-34", label: { en: "25–34", mi: null } },
            { value: "35-44", label: { en: "35–44", mi: null } },
            { value: "45-54", label: { en: "45–54", mi: null } },
            { value: "55-64", label: { en: "55–64", mi: null } },
            { value: "65-plus", label: { en: "65 or over", mi: null } },
            {
              value: "prefer-not-to-say",
              label: { en: "Prefer not to say", mi: null },
            },
          ],
        },
        {
          id: "primary_ethnicity",
          kind: "single",
          prompt: { en: "Primary ethnicity", mi: null },
          allowOther: true,
          options: [
            { value: "maori", label: { en: "Māori", mi: "Māori" } },
            {
              value: "nz-european",
              label: { en: "NZ European (Pākehā)", mi: null },
            },
            { value: "pacific", label: { en: "Pacific peoples", mi: null } },
            { value: "asian", label: { en: "Asian", mi: null } },
            {
              value: "melaa",
              label: {
                en: "Middle Eastern, Latin American or African",
                mi: null,
              },
            },
          ],
        },
        {
          id: "iwi",
          kind: "shortText",
          prompt: { en: "Iwi", mi: "Iwi" },
          maxLength: 120,
        },
        {
          id: "services_involved",
          kind: "multi",
          prompt: {
            en: "What services were you involved with during your current time with Ngāti Awa?",
            mi: null,
          },
          options: [],
          optionsSource: "service-directory",
        },
        {
          id: "time_with_service",
          kind: "single",
          prompt: { en: "How long have you been with our services?", mi: null },
          options: [
            { value: "under-6m", label: { en: "Under 6 months", mi: null } },
            { value: "6-12m", label: { en: "6–12 months", mi: null } },
            { value: "1-2y", label: { en: "1–2 years", mi: null } },
            { value: "over-2y", label: { en: "More than 2 years", mi: null } },
          ],
        },
        {
          id: "whanau_size",
          kind: "number",
          prompt: {
            en: "How many members are there in your immediate whānau / household?",
            mi: null,
          },
          min: 1,
          max: 30,
        },
        {
          id: "num_benefited",
          kind: "number",
          prompt: {
            en: "How many were involved, or benefited from our services?",
            mi: null,
          },
          min: 0,
          max: 30,
        },
      ],
    },
    {
      id: "about_experience",
      title: { en: "About your experience", mi: null },
      questions: [...RATING_QUESTIONS, ...OPEN_TEXT_QUESTIONS],
    },
    {
      id: "keep_in_touch",
      title: { en: "If you would like us to be in touch (optional)", mi: null },
      questions: [
        {
          id: "contact_name",
          kind: "shortText",
          prompt: { en: "Your name", mi: null },
          maxLength: 120,
        },
        {
          id: "contact_detail",
          kind: "shortText",
          prompt: { en: "Phone or email", mi: null },
          maxLength: 200,
        },
      ],
    },
  ],
  closing: {
    en: "Thank you for your time and responses! This will help us to improve our services to other whānau. If you would like to talk to us in person, leave your name so we can be in touch.",
    mi: null,
  },
  leaveNameNote: {
    en: "Leaving your name means your answers are no longer confidential.",
    mi: null,
  },
};

export default exitSurvey;
